import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { 
  ArrowRight, 
  Rocket, 
  Shield, 
  Palette, 
  Users, 
  Settings, 
  Target,
  Award,
  CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/services`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const getServiceIcon = (iconName) => {
    const icons = { 
      rocket: Rocket, 
      shield: Shield, 
      palette: Palette, 
      users: Users, 
      settings: Settings, 
      presentation: Award 
    };
    return icons[iconName] || Target;
  };

  if (loading) {
    return (
      <Layout title="Services" description="Business strategy, coaching, brand development, and consulting services for entrepreneurs.">
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Services" description="Business strategy, coaching, brand development, and consulting services for entrepreneurs.">
      {/* Hero Section */}
      <section className="section-padding bg-white" data-testid="services-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="overline mb-4 inline-block">Services</span>
            <h1 className="heading-1 text-4xl sm:text-5xl lg:text-6xl text-[#0A0F1C] mb-6" data-testid="services-title">
              Strategic Services to <span className="text-[#1D4ED8]">Jumpstart</span> Your Business
            </h1>
            <p className="body-text text-[#475569] text-lg mb-8">
              From idea validation to launch day, I offer comprehensive services designed to help you 
              build a legitimate, credible, and successful business.
            </p>
            <Link 
              to="/free-jumpstart-session" 
              className="btn-primary-orange inline-flex items-center gap-2"
              data-testid="services-hero-cta"
            >
              Free 20-Minute Business Jumpstart Session
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-[#FAFAFA]" data-testid="services-grid">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const Icon = getServiceIcon(service.icon);
              return (
                <div 
                  key={service.service_id || index}
                  className="bg-white p-6 border border-[#E2E8F0] rounded-lg hover:shadow-xl hover:-translate-y-1 transition-all"
                  data-testid={`service-card-${index}`}
                >
                  <div className="w-14 h-14 bg-[#EFF6FF] rounded-lg flex items-center justify-center mb-6">
                    <Icon size={28} className="text-[#1D4ED8]" />
                  </div>
                  <h2 className="heading-3 text-xl text-[#0A0F1C] mb-3">{service.title}</h2>
                  <p className="text-[#475569] mb-4 text-sm leading-relaxed">{service.short_description}</p>
                  
                  {/* Framework Stages */}
                  {service.framework_stages && service.framework_stages.length > 0 && (
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-2">
                        {service.framework_stages.slice(0, 3).map((stage, i) => (
                          <span key={i} className="badge-blue text-xs">{stage}</span>
                        ))}
                        {service.framework_stages.length > 3 && (
                          <span className="text-xs text-[#94A3B8]">+{service.framework_stages.length - 3} more</span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Features Preview */}
                  <div className="space-y-2 mb-6">
                    {service.features && service.features.slice(0, 3).map((feature, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <CheckCircle size={14} className="text-[#1D4ED8] mt-1 flex-shrink-0" />
                        <span className="text-sm text-[#475569]">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Link 
                    to={`/services/${service.slug}`}
                    className="inline-flex items-center gap-2 text-[#1D4ED8] font-bold text-sm hover:gap-3 transition-all"
                    data-testid={`service-link-${index}`}
                  >
                    Learn More <ArrowRight size={16} />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-white" data-testid="services-how-it-works">
        <div className="container-custom">
          <div className="text-center mb-8">
            <span className="overline mb-3 inline-block">How It Works</span>
            <h2 className="heading-2 text-3xl lg:text-4xl text-[#0A0F1C] mb-3">
              Your Path to Business Success
            </h2>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Book a Session', description: 'Schedule a free strategy session to discuss your goals.' },
              { step: '02', title: 'Assessment', description: 'We\'ll assess where you are and where you want to go.' },
              { step: '03', title: 'Custom Plan', description: 'Receive a tailored strategy for your business journey.' },
              { step: '04', title: 'Execute & Grow', description: 'Implement the plan with ongoing support and guidance.' },
            ].map((item, index) => (
              <div key={index} className="text-center bg-[#FAFAFA] p-5 rounded-lg border border-[#E2E8F0] rounded-lg shadow-sm" data-testid={`step-${index}`}>
                <div className="w-14 h-14 bg-[#1D4ED8] text-white font-bold text-lg rounded-lg flex items-center justify-center mx-auto mb-3">
                  {item.step}
                </div>
                <h3 className="font-bold text-[#0A0F1C] mb-2">{item.title}</h3>
                <p className="text-sm text-[#475569]">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#1D4ED8]" data-testid="services-cta">
        <div className="container-custom text-center">
          <h2 className="heading-2 text-3xl lg:text-4xl text-white mb-3">
            Ready to Get Started?
          </h2>
          <p className="text-[#BFDBFE] text-lg mb-6 max-w-2xl mx-auto">
            Book a free strategy session and let's discuss how I can help you achieve your business goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/schedule" 
              className="btn-primary-orange inline-flex items-center justify-center gap-2"
              data-testid="services-cta-primary"
            >
              FREE 20-Minute Business Jumpstart Session
              <ArrowRight size={18} />
            </Link>
            <Link 
              to="/contact" 
              className="bg-white/10 border border-white/30 text-white px-8 py-4 font-bold text-sm hover:bg-white/20 transition-colors inline-flex items-center justify-center"
              data-testid="services-cta-secondary"
            >
              Have Questions? Contact Me
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
